import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Alert, Card, Col, DatePicker, Empty, Flex, Row, Segmented, Select, Statistic, Table, Tabs, Typography } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useState } from "react";
import { useNavigate } from "react-router";
import { api, unwrap, type Schemas } from "@/api/client";
import { useI18n } from "@/i18n";
import { useUrlFilter } from "@/lib/useUrlFilter";
import { PlatformLabel } from "../channels/PlatformBadge";
import { FillCostsButton } from "./FillCosts";

type ProfitRow = Schemas["ProfitRow"];
type CashFlow = Schemas["CashFlow"];
type By = "store" | "platform" | "sku" | "month";
const TABS = ["profit", "cash", "aging"] as const;
const BYS: By[] = ["store", "platform", "sku", "month"];
const DAY = "YYYY-MM-DD";

function percent(bp: number) {
  return `${(bp / 100).toFixed(1).replace(".", ",")}%`;
}

function useRange() {
  return useState<[Dayjs, Dayjs]>(() => [dayjs().startOf("month"), dayjs()]);
}

/** Keuangan > Laporan (Plan §6.19 part E): gross profit, cash flow, aging. */
export function Reports() {
  const { t } = useI18n();
  const [tab, setTab] = useUrlFilter<(typeof TABS)[number]>("tab", "profit", TABS);
  return (
    <>
      <Typography.Title level={2} style={{ margin: "0 0 16px", fontSize: 28 }}>
        {t("report.title")}
      </Typography.Title>
      <Tabs
        activeKey={tab}
        onChange={(k) => setTab(k as (typeof TABS)[number])}
        destroyOnHidden
        items={[
          { key: "profit", label: t("report.profit"), children: <GrossProfit /> },
          { key: "cash", label: t("report.cash"), children: <CashFlowReport /> },
          { key: "aging", label: t("report.aging"), children: <Aging /> },
        ]}
      />
    </>
  );
}

function GrossProfit() {
  const { t, formatMoney, formatNumber } = useI18n();
  const [range, setRange] = useRange();
  const [by, setBy] = useUrlFilter<By>("by", "store", BYS);
  const query = { from: range[0].format(DAY), to: range[1].format(DAY), by };
  const report = useQuery({
    queryKey: ["finance", "reports", "profit", query],
    queryFn: () => unwrap(api.GET("/v1/finance/reports/gross-profit", { params: { query } })),
    placeholderData: keepPreviousData,
  });
  const total = report.data?.total;
  const money = (v: number) => <span className="z-num">{formatMoney(v)}</span>;
  const label = (r: ProfitRow) => {
    if (by === "platform") return <PlatformLabel platform={r.key as "shopee"} />;
    if (by === "month") return dayjs(`${r.key}-01`).format("MMM YYYY");
    if (by === "sku" && !r.key) return t("report.unmapped");
    return r.label;
  };
  return (
    <>
      <Flex gap={12} wrap align="center" style={{ marginBottom: 16 }}>
        <DatePicker.RangePicker value={range} allowClear={false} onChange={(v) => v && v[0] && v[1] && setRange([v[0], v[1]])} format="DD/MM/YYYY" />
        <Segmented<By> value={by} onChange={setBy} options={BYS.map((b) => ({ value: b, label: t(`report.by.${b}`) }))} />
      </Flex>
      <Typography.Paragraph type="secondary">{t("report.profitHelp")}</Typography.Paragraph>
      {total && (
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={12} lg={6}>
            <Card size="small">
              <Statistic title={t("report.revenue")} value={formatMoney(total.revenue)} />
            </Card>
          </Col>
          <Col xs={12} lg={6}>
            <Card size="small">
              <Statistic title={t("report.cost")} value={formatMoney(total.cost)} />
            </Card>
          </Col>
          <Col xs={12} lg={6}>
            <Card size="small">
              <Statistic title={t("report.grossProfit")} value={formatMoney(total.grossProfit)} />
              <Typography.Text type="secondary">{t("report.margin", { pct: percent(total.marginBp) })}</Typography.Text>
            </Card>
          </Col>
          <Col xs={12} lg={6}>
            <Card size="small">
              <Statistic title={t("report.afterFees")} value={formatMoney(total.afterFees)} />
              <Typography.Text type="secondary">{t("report.feesLine", { fees: formatMoney(-total.fees) })}</Typography.Text>
            </Card>
          </Col>
        </Row>
      )}
      {report.data && (report.data.stockVariance.loss > 0 || report.data.stockVariance.gain > 0 || report.data.stockVariance.damaged > 0 || report.data.stockVariance.lost > 0 || report.data.stockVariance.recovered > 0 || report.data.stockVariance.unitsNoCost > 0) && total && (
        <Card size="small" title={t("report.stockVariance")} style={{ marginBottom: 16 }}>
          <Typography.Paragraph type="secondary">{t("report.stockVarianceHelp")}</Typography.Paragraph>
          <Row gutter={[16, 16]}>
            <Col xs={12} lg={6}>
              <Statistic title={t("report.varianceLoss")} value={formatMoney(-report.data.stockVariance.loss)} />
            </Col>
            <Col xs={12} lg={6}>
              <Statistic title={t("report.varianceGain")} value={formatMoney(report.data.stockVariance.gain)} />
            </Col>
            <Col xs={12} lg={6}>
              <Statistic title={t("report.damaged")} value={formatMoney(-report.data.stockVariance.damaged)} />
            </Col>
            <Col xs={12} lg={6}>
              <Statistic title={t("report.lost")} value={formatMoney(-report.data.stockVariance.lost)} />
            </Col>
            <Col xs={12} lg={6}>
              <Statistic title={t("report.recovered")} value={formatMoney(report.data.stockVariance.recovered)} />
            </Col>
            <Col xs={12} lg={6}>
              <Statistic title={t("report.afterVariance")} value={formatMoney(total.afterFees + report.data.stockVariance.net)} />
            </Col>
          </Row>
          {report.data.stockVariance.unitsNoCost > 0 && (
            <Alert type="warning" showIcon style={{ marginTop: 12 }} title={t("report.varianceNoCost", { n: formatNumber(report.data.stockVariance.unitsNoCost) })} action={<FillCostsButton />} />
          )}
        </Card>
      )}
      {report.data && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          title={t("report.basis")}
          description={
            report.data.inTransit.orders > 0
              ? t("report.inTransit", { n: formatNumber(report.data.inTransit.orders), value: formatMoney(report.data.inTransit.value) })
              : undefined
          }
        />
      )}
      {total && total.unitsNoCost > 0 && <Alert type="warning" showIcon style={{ marginBottom: 16 }} title={t("report.noCost", { n: formatNumber(total.unitsNoCost) })} action={<FillCostsButton />} />}
      <Table<ProfitRow>
        rowKey="key"
        size="small"
        loading={report.isFetching}
        dataSource={report.data?.rows ?? []}
        pagination={(report.data?.rows.length ?? 0) > 50 ? { pageSize: 50, showSizeChanger: false } : false}
        scroll={{ x: 1000 }}
        locale={{ emptyText: <Empty description={t("report.profitEmpty")} /> }}
        columns={[
          { title: t(`report.by.${by}`), key: "label", render: (_, r) => label(r) },
          ...(by === "sku" ? [] : [{ title: t("report.orders"), dataIndex: "orders", width: 90, align: "right" as const, render: (v: number) => <span className="z-num">{formatNumber(v)}</span> }]),
          { title: t("report.units"), dataIndex: "units", width: 80, align: "right", render: (v: number) => <span className="z-num">{formatNumber(v)}</span> },
          { title: t("report.revenue"), dataIndex: "revenue", width: 140, align: "right", render: money },
          { title: t("report.cost"), dataIndex: "cost", width: 140, align: "right", render: money },
          { title: t("report.grossProfit"), dataIndex: "grossProfit", width: 140, align: "right", render: money },
          { title: t("report.marginCol"), dataIndex: "marginBp", width: 90, align: "right", render: (v: number) => <span className="z-num">{percent(v)}</span> },
          ...(by === "sku"
            ? []
            : [
                { title: t("report.fees"), dataIndex: "fees", width: 130, align: "right" as const, render: (v: number) => money(-v) },
                { title: t("report.afterFees"), dataIndex: "afterFees", width: 140, align: "right" as const, render: money },
              ]),
        ]}
      />
    </>
  );
}

function CashFlowReport() {
  const { t, formatMoney } = useI18n();
  const [range, setRange] = useRange();
  const [account, setAccount] = useUrlFilter<string>("account", "");
  const accounts = useQuery({ queryKey: ["finance", "cash", "accounts"], queryFn: () => unwrap(api.GET("/v1/finance/cash/accounts")) });
  const query = { from: range[0].format(DAY), to: range[1].format(DAY), ...(account ? { accountId: account } : {}) };
  const report = useQuery({
    queryKey: ["finance", "reports", "cash", query],
    queryFn: () => unwrap(api.GET("/v1/finance/reports/cash-flow", { params: { query } })),
    placeholderData: keepPreviousData,
  });
  const cf = report.data;
  const money = (v: number) => <span className="z-num">{formatMoney(v)}</span>;
  return (
    <>
      <Flex gap={12} wrap align="center" style={{ marginBottom: 16 }}>
        <DatePicker.RangePicker value={range} allowClear={false} onChange={(v) => v && v[0] && v[1] && setRange([v[0], v[1]])} format="DD/MM/YYYY" />
        <Select<string>
          value={account}
          onChange={setAccount}
          style={{ width: 220 }}
          options={[{ value: "", label: t("cash.allAccounts") }, ...(accounts.data?.items ?? []).map((a) => ({ value: a.id, label: a.name }))]}
        />
      </Flex>
      <Typography.Paragraph type="secondary">{t(account ? "report.cashHelpAccount" : "report.cashHelp")}</Typography.Paragraph>
      {cf && (
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={12} lg={6}>
            <Card size="small">
              <Statistic title={t("report.opening")} value={formatMoney(cf.opening + cf.started)} />
              {cf.started > 0 && <Typography.Text type="secondary">{t("report.started", { amount: formatMoney(cf.started) })}</Typography.Text>}
            </Card>
          </Col>
          <Col xs={12} lg={6}>
            <Card size="small">
              <Statistic title={t("report.moneyIn")} value={formatMoney(cf.moneyIn)} styles={{ content: { color: "var(--ant-color-success)" } }} />
            </Card>
          </Col>
          <Col xs={12} lg={6}>
            <Card size="small">
              <Statistic title={t("report.moneyOut")} value={formatMoney(cf.moneyOut)} styles={{ content: { color: "var(--ant-color-error)" } }} />
            </Card>
          </Col>
          <Col xs={12} lg={6}>
            <Card size="small">
              <Statistic title={t("report.closing")} value={formatMoney(cf.closing)} />
              <Typography.Text type="secondary">{t("report.net", { amount: formatMoney(cf.moneyIn - cf.moneyOut) })}</Typography.Text>
            </Card>
          </Col>
        </Row>
      )}
      <Row gutter={[16, 16]}>
        <Col xs={24} xl={14}>
          <Card size="small" title={t("report.byCategory")}>
            <Table<CashFlow["categories"][number]>
              size="small"
              rowKey={(c) => `${c.kind}|${c.category}`}
              loading={report.isFetching}
              dataSource={cf?.categories ?? []}
              pagination={false}
              locale={{ emptyText: t("report.cashEmpty") }}
              columns={[
                { title: t("cash.kindLabel"), dataIndex: "kind", render: (k: CashFlow["categories"][number]["kind"]) => t(`cash.entryKind.${k}`) },
                { title: t("cash.category"), dataIndex: "category", render: (v: string) => v || "—" },
                { title: t("report.moneyIn"), dataIndex: "moneyIn", width: 140, align: "right", render: (v: number) => (v ? money(v) : "—") },
                { title: t("report.moneyOut"), dataIndex: "moneyOut", width: 140, align: "right", render: (v: number) => (v ? money(v) : "—") },
              ]}
            />
          </Card>
        </Col>
        <Col xs={24} xl={10}>
          <Card size="small" title={t("report.byMonth")} style={{ marginBottom: 16 }}>
            <Table<CashFlow["months"][number]>
              size="small"
              rowKey="month"
              dataSource={cf?.months ?? []}
              pagination={false}
              locale={{ emptyText: t("report.cashEmpty") }}
              columns={[
                { title: t("report.month"), dataIndex: "month", render: (v: string) => dayjs(`${v}-01`).format("MMM YYYY") },
                { title: t("report.moneyIn"), dataIndex: "moneyIn", align: "right", render: money },
                { title: t("report.moneyOut"), dataIndex: "moneyOut", align: "right", render: money },
                {
                  title: t("report.netCol"),
                  dataIndex: "net",
                  align: "right",
                  render: (v: number) => <Typography.Text className="z-num" type={v < 0 ? "danger" : undefined}>{formatMoney(v)}</Typography.Text>,
                },
              ]}
            />
          </Card>
          <Card size="small" title={t("report.byAccount")}>
            <Table<CashFlow["accounts"][number]>
              size="small"
              rowKey="id"
              dataSource={cf?.accounts ?? []}
              pagination={false}
              columns={[
                { title: t("cash.account"), dataIndex: "name" },
                { title: t("report.opening"), key: "open", align: "right", render: (_, a) => money(a.opening + a.started) },
                { title: t("report.closing"), dataIndex: "closing", align: "right", render: money },
              ]}
            />
          </Card>
        </Col>
      </Row>
    </>
  );
}

function Aging() {
  const { t, formatMoney, formatNumber } = useI18n();
  const navigate = useNavigate();
  const payables = useQuery({ queryKey: ["finance", "payables"], queryFn: () => unwrap(api.GET("/v1/finance/payables")) });
  const receivables = useQuery({ queryKey: ["finance", "receivables", "aging"], queryFn: () => unwrap(api.GET("/v1/finance/receivables")) });
  const ap = payables.data;
  const ar = (receivables.data?.items ?? []).filter((s) => s.unsettledAmount > 0 || s.balance !== 0);
  const money = (v: number) => <span className="z-num">{v ? formatMoney(v) : "—"}</span>;
  const arTotal = ar.reduce((s, x) => s + x.unsettledAmount, 0);
  return (
    <>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} lg={8}>
          <Card size="small" loading={payables.isPending}>
            <Statistic title={t("report.apTotal")} value={formatMoney(ap?.outstanding ?? 0)} />
            <Typography.Text type="secondary">{t("report.apOverdue", { amount: formatMoney(ap?.overdue ?? 0) })}</Typography.Text>
          </Card>
        </Col>
        <Col xs={12} lg={8}>
          <Card size="small" loading={receivables.isPending}>
            <Statistic title={t("report.arTotal")} value={formatMoney(arTotal)} />
            <Typography.Text type="secondary">{t("report.arHelp")}</Typography.Text>
          </Card>
        </Col>
        <Col xs={12} lg={8}>
          <Card size="small" loading={payables.isPending} hoverable onClick={() => navigate("/app/finance/credits")}>
            <Statistic title={t("report.credits")} value={formatMoney(ap?.credits ?? 0)} />
            <Typography.Text type="secondary">{t("report.creditsHelp", { n: formatNumber(ap?.creditNotes ?? 0) })}</Typography.Text>
          </Card>
        </Col>
      </Row>
      <Card size="small" title={t("report.apAging")} style={{ marginBottom: 16 }}>
        <Table
          size="small"
          rowKey="supplierId"
          loading={payables.isPending}
          dataSource={ap?.aging ?? []}
          pagination={(ap?.aging.length ?? 0) > 20 ? { pageSize: 20 } : false}
          scroll={{ x: 900 }}
          locale={{ emptyText: t("report.apEmpty") }}
          onRow={(a) => ({ onClick: () => navigate(`/app/finance/bills?supplierId=${a.supplierId}&status=unpaid`), style: { cursor: "pointer" } })}
          columns={[
            { title: t("po.supplier"), dataIndex: "supplierName" },
            { title: t("bill.agingCurrent"), dataIndex: "current", width: 120, align: "right", render: money },
            { title: t("bill.aging1to30"), dataIndex: "days1to30", width: 120, align: "right", render: money },
            { title: t("bill.aging31to60"), dataIndex: "days31to60", width: 120, align: "right", render: money },
            { title: t("bill.aging61to90"), dataIndex: "days61to90", width: 120, align: "right", render: money },
            { title: t("bill.agingOver90"), dataIndex: "daysOver90", width: 120, align: "right", render: money },
            { title: t("report.total"), dataIndex: "outstanding", width: 140, align: "right", render: (v: number) => <Typography.Text strong className="z-num">{formatMoney(v)}</Typography.Text> },
          ]}
        />
      </Card>
      <Card size="small" title={t("report.arAging")}>
        <Table
          size="small"
          rowKey="connectionId"
          loading={receivables.isPending}
          dataSource={ar}
          pagination={false}
          scroll={{ x: 900 }}
          locale={{ emptyText: t("report.arEmpty") }}
          onRow={() => ({ onClick: () => navigate("/app/finance/receivables"), style: { cursor: "pointer" } })}
          columns={[
            { title: t("report.by.store"), dataIndex: "name", render: (v: string, s) => <Flex gap={6} align="center">{v} <Typography.Text type="secondary" style={{ fontSize: 12 }}>{t(`platform.${s.platform}`)}</Typography.Text></Flex> },
            { title: t("report.ar.d7"), key: "d7", width: 120, align: "right", render: (_, s) => money(s.aging.days7) },
            { title: t("report.ar.d14"), key: "d14", width: 120, align: "right", render: (_, s) => money(s.aging.days14) },
            { title: t("report.ar.d30"), key: "d30", width: 120, align: "right", render: (_, s) => money(s.aging.days30) },
            { title: t("report.ar.older"), key: "older", width: 120, align: "right", render: (_, s) => money(s.aging.older) },
            { title: t("report.total"), dataIndex: "unsettledAmount", width: 140, align: "right", render: (v: number) => <Typography.Text strong className="z-num">{formatMoney(v)}</Typography.Text> },
            { title: t("report.balance"), dataIndex: "balance", width: 130, align: "right", render: money },
          ]}
        />
      </Card>
    </>
  );
}
